/**
 * Therapist daily schedule — /therapy/schedule
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { CheckCircle, Cancel, EditNote, CalendarMonth, ContentCopy } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { therapyAPI } from '../../services/api';
import AppHeader from '../AppHeader';

const formatTime = (t) => {
  if (!t) return '—';
  const [h, m] = t.split(':');
  const hour = Number(h);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const h12 = hour % 12 || 12;
  return `${h12}:${m?.slice(0, 2) || '00'} ${ampm}`;
};

const statusColor = (status) => {
  switch (status) {
    case 'completed': return 'success';
    case 'missed_absent':
    case 'missed_other': return 'error';
    case 'makeup_scheduled': return 'info';
    default: return 'default';
  }
};

function MakeupDialog({ open, session, onClose, onScheduled }) {
  const [candidates, setCandidates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [leaveEarly, setLeaveEarly] = useState(null);

  useEffect(() => {
    if (!open || !session) return;
    setLoading(true);
    therapyAPI.getMakeupSlots(session.id)
      .then((res) => {
        setCandidates(res.data.candidates || []);
        setLeaveEarly(res.data.therapist_can_leave_early ? res.data.earliest_leave_time : null);
      })
      .finally(() => setLoading(false));
  }, [open, session]);

  const schedule = async (c) => {
    try {
      await therapyAPI.scheduleMakeup(session.id, {
        date: c.date,
        start_time: c.start_time,
        duration_minutes: c.duration_minutes,
      });
      onScheduled();
      onClose();
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to schedule makeup');
    }
  };

  const conflictColor = (s) => (s === 'clear' ? 'success' : s === 'tight' ? 'warning' : 'error');

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Schedule makeup</DialogTitle>
      <DialogContent dividers>
        {loading ? <CircularProgress size={24} /> : (
          <>
            {candidates.length === 0 ? (
              <Alert severity="info">
                No makeup possible this week
                {leaveEarly ? ` — you may end your day at ${formatTime(leaveEarly)}` : ''}
              </Alert>
            ) : (
              candidates.map((c, i) => (
                <Box key={i} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1, borderBottom: 1, borderColor: 'divider' }}>
                  <Box>
                    <Typography variant="body2">{c.date} · {formatTime(c.start_time)}</Typography>
                    <Chip label={c.conflict_status} size="small" color={conflictColor(c.conflict_status)} sx={{ mt: 0.5 }} />
                    {c.schedule_shift_possible && (
                      <Typography variant="caption" display="block" color="text.secondary">May shift other sessions</Typography>
                    )}
                  </Box>
                  <Button size="small" variant="outlined" disabled={c.conflict_status === 'blocked'} onClick={() => schedule(c)}>
                    Confirm
                  </Button>
                </Box>
              ))
            )}
          </>
        )}
      </DialogContent>
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function CalendarSubscribeDialog({ open, onClose }) {
  const [loading, setLoading] = useState(false);
  const [subscriptionUrl, setSubscriptionUrl] = useState('');
  const [copied, setCopied] = useState(false);

  const loadToken = useCallback(async () => {
    setLoading(true);
    try {
      const res = await therapyAPI.getCalendarToken();
      setSubscriptionUrl(res.data.subscription_url || '');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to load calendar link');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (open) loadToken();
  }, [open, loadToken]);

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(subscriptionUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      alert('Copy failed — select and copy the link manually');
    }
  };

  const rotate = async () => {
    setLoading(true);
    try {
      const res = await therapyAPI.rotateCalendarToken();
      setSubscriptionUrl(res.data.subscription_url || '');
    } catch (e) {
      alert(e.response?.data?.detail || 'Failed to rotate link');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Add your therapy schedule to your calendar</DialogTitle>
      <DialogContent dividers>
        {loading ? <CircularProgress size={24} /> : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              Copy the link below and add it in Google Calendar, Outlook, or Apple Calendar via &quot;Subscribe from URL&quot;.
              Updates appear within a few hours.
            </DialogContentText>
            <TextField fullWidth size="small" value={subscriptionUrl} InputProps={{ readOnly: true }}
              sx={{ mb: 2, fontFamily: 'monospace', fontSize: 12 }} />
            <Button startIcon={<ContentCopy />} onClick={copyLink} size="small">
              {copied ? 'Copied!' : 'Copy link'}
            </Button>
            <Alert severity="warning" sx={{ mt: 2 }}>
              Do not share this link — it exposes your full schedule including student names.
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={rotate} disabled={loading}>Rotate link</Button>
        <Button variant="contained" onClick={onClose}>Done</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function TherapySchedulePage() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [date, setDate] = useState(today);
  const [sessions, setSessions] = useState([]);
  const [myStudents, setMyStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [noteDialog, setNoteDialog] = useState({ open: false, session: null, text: '' });
  const [makeupSession, setMakeupSession] = useState(null);
  const [calendarOpen, setCalendarOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await therapyAPI.getTherapistSchedule(user?.id, date);
      setSessions(res.data.sessions || []);
      setMyStudents(res.data.my_students || []);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || 'Failed to load schedule');
    } finally {
      setLoading(false);
    }
  }, [user?.id, date]);

  useEffect(() => { load(); }, [load]);

  const markComplete = async (id) => {
    await therapyAPI.completeSession(id);
    load();
  };

  const markMissed = async (id) => {
    await therapyAPI.markMissed(id, { reason: 'other' });
    load();
  };

  const saveNote = async () => {
    const { session, text } = noteDialog;
    if (!session) return;
    await therapyAPI.completeSession(session.id, { notes: text });
    setNoteDialog({ open: false, session: null, text: '' });
    load();
  };

  return (
    <>
      <AppHeader title="Therapy Schedule" />
      <Container maxWidth="lg" sx={{ py: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2, flexWrap: 'wrap' }}>
          <TextField size="small" type="date" label="Date" InputLabelProps={{ shrink: true }} value={date}
            onChange={(e) => setDate(e.target.value)} />
          <Typography variant="body2" color="text.secondary">{user?.email}</Typography>
          <Box sx={{ flexGrow: 1 }} />
          <Tooltip title="Subscribe in Google Calendar, Outlook, or Apple Calendar">
            <Button startIcon={<CalendarMonth />} variant="outlined" onClick={() => setCalendarOpen(true)}>
              Subscribe to My Calendar
            </Button>
          </Tooltip>
        </Box>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Grid container spacing={2}>
          <Grid item xs={12} md={9}>
            <Paper>
              {loading ? (
                <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
              ) : sessions.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No sessions scheduled for this date.</Typography>
                </Box>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Time</TableCell>
                        <TableCell>Student</TableCell>
                        <TableCell>Service</TableCell>
                        <TableCell>Location</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Actions</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {sessions.map((s) => (
                        <TableRow key={s.id} sx={s.student_absent_today ? { bgcolor: 'warning.light' } : {}}>
                          <TableCell>{formatTime(s.start_time)}</TableCell>
                          <TableCell>
                            {s.student_name}
                            {s.student_absent_today && (
                              <Chip label="ABSENT" size="small" color="warning" sx={{ ml: 1 }} />
                            )}
                          </TableCell>
                          <TableCell>{s.service_label}</TableCell>
                          <TableCell>{s.location || '—'}</TableCell>
                          <TableCell>
                            <Chip label={s.status.replace(/_/g, ' ')} size="small" color={statusColor(s.status)} />
                          </TableCell>
                          <TableCell>
                            {['scheduled', 'missed_absent', 'missed_other'].includes(s.status) && (
                              <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                <Button size="small" color="success" startIcon={<CheckCircle />} onClick={() => markComplete(s.id)}>Complete</Button>
                                <Button size="small" color="error" startIcon={<Cancel />} onClick={() => markMissed(s.id)}>Missed</Button>
                                <Button size="small" startIcon={<EditNote />} onClick={() => setNoteDialog({ open: true, session: s, text: s.notes || '' })}>Note</Button>
                                {(s.status === 'missed_absent' || s.status === 'missed_other') && (
                                  <Button size="small" variant="outlined" onClick={() => setMakeupSession(s)}>Schedule makeup</Button>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle1" gutterBottom>My Students</Typography>
              {myStudents.length === 0 ? (
                <Typography variant="body2" color="text.secondary">No active caseload.</Typography>
              ) : (
                <List dense disablePadding>
                  {myStudents.map((st) => (
                    <ListItem key={st.id} disablePadding>
                      <ListItemButton component={RouterLink} to={`/students/${st.id}?tab=services`}>
                        <ListItemText primary={`${st.first_name} ${st.last_name}`.trim()} />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Paper>
          </Grid>
        </Grid>

        <Dialog open={noteDialog.open} onClose={() => setNoteDialog({ open: false, session: null, text: '' })}>
          <DialogTitle>Session note</DialogTitle>
          <DialogContent>
            <TextField fullWidth multiline minRows={3} value={noteDialog.text}
              onChange={(e) => setNoteDialog((p) => ({ ...p, text: e.target.value }))} sx={{ mt: 1 }} />
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setNoteDialog({ open: false, session: null, text: '' })}>Cancel</Button>
            <Button variant="contained" onClick={saveNote}>Save</Button>
          </DialogActions>
        </Dialog>

        <MakeupDialog open={!!makeupSession} session={makeupSession} onClose={() => setMakeupSession(null)} onScheduled={load} />
        <CalendarSubscribeDialog open={calendarOpen} onClose={() => setCalendarOpen(false)} />
      </Container>
    </>
  );
}
